/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter
} from '@reduxjs/toolkit';
import {
  GetDepartmentQueryVariables,
  LearnedAnswer,
  LearnedAnswerConnection,
  ListDepartmentLearnedAnswersQuery
} from '@/generated/graphql';
import { AsyncThunkConfig, RootState } from '@/stores/AppStore';
import Queries from '@/graphql/Queries';
import { FetchResult } from '@apollo/client';

export interface GetDepartmentLearnedAnswersParams
  extends GetDepartmentQueryVariables {
  limit: number;
  page?: number;
  nextToken?: string;
  reset?: boolean;
}

interface IGetDepartmentLearnedAnswersData {
  getDepartment: ListDepartmentLearnedAnswersQuery['getDepartment'];
}

export const getDepartmentLearnedAnswers = createAsyncThunk<
  FetchResult<IGetDepartmentLearnedAnswersData>,
  GetDepartmentLearnedAnswersParams,
  AsyncThunkConfig
>(
  'departmentLearnedAnswers/fetchAll',
  async ({ id, limit, nextToken, reset }, thunkAPI) =>
    thunkAPI.extra.appSyncClient.query({
      query: Queries.ListDepartmentLearnedAnswers(),
      variables: {
        id,
        limit,
        nextToken: reset ? undefined : nextToken
      }
    })
);

const DepartmentLearnedAnswersAdapter = createEntityAdapter<LearnedAnswer>({
  selectId: (learning) => learning.id!
});

interface IState {
  loading: boolean;
  nextToken: string | undefined;
  error: any;
  pageDataMap: Record<number, LearnedAnswer[]>;
}

export const departmentLearnedAnswersSlice = createSlice({
  name: 'departmentLearnedAnswers',
  initialState: DepartmentLearnedAnswersAdapter.getInitialState<IState>({
    loading: false,
    nextToken: undefined,
    error: undefined,
    pageDataMap: {}
  }),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getDepartmentLearnedAnswers.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getDepartmentLearnedAnswers.fulfilled,
      (state, { payload, meta }) => {
        if (payload?.data?.getDepartment?.learnedAnswers) {
          const learnedAnswersConnection = payload.data.getDepartment
            .learnedAnswers as LearnedAnswerConnection;
          const learnedAnswers =
            learnedAnswersConnection.items as LearnedAnswer[];

          if (meta.arg.reset) {
            DepartmentLearnedAnswersAdapter.setAll(state, learnedAnswers);
          } else {
            DepartmentLearnedAnswersAdapter.addMany(state, learnedAnswers);
          }

          if (meta.arg.reset) {
            state.pageDataMap = {
              [meta.arg.page!]: learnedAnswers
            };
          } else {
            if (meta.arg.page && learnedAnswers.length > 0) {
              state.pageDataMap[meta.arg.page] = learnedAnswers;
            }
          }

          state.nextToken = learnedAnswersConnection.nextToken || undefined;
        }

        state.loading = false;
      }
    );
    builder.addCase(
      getDepartmentLearnedAnswers.rejected,
      (state, { payload }) => {
        state.error = payload as any;
        state.loading = false;
        state.nextToken = undefined;
      }
    );
  }
});

export const departmentLearnedAnswersSelector =
  DepartmentLearnedAnswersAdapter.getSelectors<RootState>(
    (state) => state.departmentLearnedAnswers
  );

export const departmentLearnedAnswersQuerySelector = (state: RootState) => ({
  loading: state.departmentLearnedAnswers.loading,
  learnedAnswers: departmentLearnedAnswersSelector.selectAll(state),
  nextToken: state.departmentLearnedAnswers.nextToken,
  pageDataMap: state.departmentLearnedAnswers.pageDataMap,
  error: state.departmentLearnedAnswers.error
});

export default departmentLearnedAnswersSlice.reducer;
